<div class="wrapper">
  <div class="max-w-full">
    <div
      class="bg-gradient-to-r from-primary via-blue-500 to-blue-600 text-white relative overflow-hidden"
    >
      <!-- Decorative shapes -->
      <div
        class="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-48 -mt-48"
      ></div>
      <div
        class="absolute bottom-0 left-0 w-80 h-80 bg-white opacity-5 rounded-full -ml-40 -mb-40"
      ></div>

      <div class="container mx-auto px-4 py-12 relative z-10">
        <!-- Breadcrumb Navigation -->
        <nav
          aria-label="You are here:"
          role="navigation"
          class="daisy-breadcrumbs mb-6"
        >
          <ul class="text-blue-100">
            <li>
              <a href="#" class="hover:text-white transition-colors">Home</a>
            </li>
            <li>
              <a href="#" class="hover:text-white transition-colors"
                >Live Classes</a
              >
            </li>
            <li class="text-white font-medium">Create New Class</li>
          </ul>
        </nav>

        <!-- Main Header -->
        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-3">
            <div
              class="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center"
            >
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <h1 class="text-4xl md:text-5xl font-bold tracking-tight">
              Create New Live Class
            </h1>
          </div>
          <p class="text-blue-100 text-lg max-w-2xl">
            Set up your interactive live class with detailed information,
            schedules, and settings to engage your students
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="container mx-auto px-4">
    <div class="flex flex-wrap px-4">
      <div class="w-full">
        <black-stepper
          [section]="1"
          [steps]="['Create', 'Preview & Submit']"
        ></black-stepper>
      </div>
    </div>

    <div class="flex flex-wrap">
      <div class="w-full md:w-10/12 md:ml-[8.333333%]">
        @if (isPublished) {
          <div class="daisy-alert daisy-alert-warning mb-6">
            <i class="fa-solid fa-lock text-base"></i>
            <div>
              <h4 class="font-bold">Published Class (Restricted Fields)</h4>
              <p class="text-xs">Start date, end date, price per session, and schedule are locked for published classes to protect enrolled students.</p>
            </div>
          </div>
        }

        <form [formGroup]="createLiveClassForm" id="step1">
          <div id="course-info" formGroupName="basic">
            <h4 class="text-2xl font-bold text-base-content">
              Basic Information
            </h4>
            <div class="container mt-4">
              <div class="flex flex-wrap">
                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">Title</legend>
                    <input
                      type="text"
                      class="daisy-input daisy-input-bordered w-full"
                      formControlName="title"
                      placeholder="e.g. Grade 10 Mathematics – Term 2 Live Class"
                      required
                    />
                  </fieldset>
                </div>
                <div class="w-full mt-4 mb-4">
                  <label class="font-semibold text-base-content block mb-1">
                    Description
                  </label>
                  <black-rich-text-editor
                    formControlName="description"
                    placeholder="Enter detailed class description..."
                    theme="snow"
                    minHeight="240px"
                  ></black-rich-text-editor>
                </div>
                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      Description Summary
                      <black-tooltip
                        >A summary of the description added above</black-tooltip
                      >
                    </legend>
                    <textarea
                      class="daisy-textarea daisy-textarea-bordered w-full"
                      formControlName="summary"
                      rows="5"
                      placeholder="e.g. A concise overview of what students will learn in this class, covering key topics and objectives."
                    >
                    </textarea>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>

          <div
            id="course-additional-info"
            formGroupName="information"
            class="mt-8"
          >
            <h4 class="text-2xl font-bold text-base-content">
              Additional Information
            </h4>
            <div class="container mt-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="w-full">
                  <div class="flex items-center justify-between mb-1.5">
                    <label class="font-semibold text-sm text-base-content flex items-center gap-1.5">
                      <span>Starts On (Included)</span>
                      <black-tooltip
                        >A scheduled date when this course will start and be
                        accessible to students</black-tooltip
                      >
                    </label>
                    @if (isPublished) {
                      <span
                        class="daisy-badge daisy-badge-warning daisy-badge-sm gap-1 font-medium shrink-0"
                      >
                        <i class="fa-solid fa-lock text-[10px]"></i> Locked
                      </span>
                    }
                  </div>
                  <input
                    class="daisy-input daisy-input-bordered w-full"
                    [ngClass]="{
                      'bg-base-200 cursor-not-allowed opacity-80': isPublished,
                    }"
                    formControlName="startDate"
                    type="date"
                  />
                </div>
                <div class="w-full">
                  <div class="flex items-center justify-between mb-1.5">
                    <label class="font-semibold text-sm text-base-content flex items-center gap-1.5">
                      <span>Ends On (Included)</span>
                      <black-tooltip
                        >The final scheduled date for this course (sessions on
                        this date are included)</black-tooltip
                      >
                    </label>
                    @if (isPublished) {
                      <span
                        class="daisy-badge daisy-badge-warning daisy-badge-sm gap-1 font-medium shrink-0"
                      >
                        <i class="fa-solid fa-lock text-[10px]"></i> Locked
                      </span>
                    }
                  </div>
                  <input
                    class="daisy-input daisy-input-bordered w-full"
                    [ngClass]="{
                      'bg-base-200 cursor-not-allowed opacity-80': isPublished,
                    }"
                    formControlName="endDate"
                    type="date"
                  />
                </div>
                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      Language
                      <black-tooltip
                        >The primary language used in this course</black-tooltip
                      >
                    </legend>
                    <select
                      class="daisy-select daisy-select-bordered w-full"
                      formControlName="medium"
                    >
                      <option value="ENGLISH">English</option>
                      <option selected value="TAMIL">Tamil</option>
                      <option value="SINHALA">Sinhala</option>
                    </select>
                  </fieldset>
                </div>

                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      Class Mode
                      <black-tooltip
                        >Select the delivery mode for this live
                        class</black-tooltip
                      >
                    </legend>
                    <select
                      class="daisy-select daisy-select-bordered w-full"
                      formControlName="classMode"
                    >
                      <option value="ONLINE">Online</option>
                      <option value="HYBRID">Hybrid</option>
                    </select>
                  </fieldset>
                </div>

                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      Target Audience
                      <black-tooltip
                        >Select the intended audience for this
                        class</black-tooltip
                      >
                    </legend>
                    <select
                      class="daisy-select daisy-select-bordered w-full"
                      formControlName="targetAudience"
                    >
                      <option value="" disabled>Select target audience</option>
                      @for (option of targetAudienceOptions; track option) {
                        <option [value]="option">{{ option }}</option>
                      }
                      <option value="Other">Other (specify below)</option>
                    </select>
                  </fieldset>
                  @if (
                    formControls.information.targetAudience.value === 'Other'
                  ) {
                    <fieldset class="daisy-fieldset mt-2">
                      <legend class="daisy-fieldset-legend">
                        Specify Target Audience
                      </legend>
                      <input
                        type="text"
                        class="daisy-input daisy-input-bordered w-full"
                        formControlName="targetAudienceOther"
                        placeholder="e.g. Adult Learners, Working Professionals"
                      />
                    </fieldset>
                  }
                </div>

                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      Subject
                      <black-tooltip
                        >The subject or topic area covered in this
                        class</black-tooltip
                      >
                    </legend>
                    @if (subjectOptions.length > 0) {
                      <select
                        class="daisy-select daisy-select-bordered w-full"
                        formControlName="subject"
                      >
                        <option value="" disabled>Select a subject</option>
                        @for (option of subjectOptions; track option) {
                          <option [value]="option">{{ option }}</option>
                        }
                        <option value="Other">Other (specify below)</option>
                      </select>
                    } @else {
                      <input
                        type="text"
                        class="daisy-input daisy-input-bordered w-full"
                        formControlName="subject"
                        placeholder="e.g. Mathematics, Physics, English Literature"
                      />
                    }
                  </fieldset>
                  @if (formControls.information.subject.value === 'Other') {
                    <fieldset class="daisy-fieldset mt-2">
                      <legend class="daisy-fieldset-legend">
                        Specify Subject
                      </legend>
                      <input
                        type="text"
                        class="daisy-input daisy-input-bordered w-full"
                        formControlName="subjectOther"
                        placeholder="e.g. Environmental Science, Creative Writing"
                      />
                    </fieldset>
                  }
                </div>
              </div>

              <div
                class="w-full mt-4"
                id="form-array"
                formArrayName="requirements"
              >
                <fieldset class="daisy-fieldset">
                  <legend class="daisy-fieldset-legend">
                    Requirements
                    <black-tooltip
                      >Pre requirements that the students should
                      meet</black-tooltip
                    >
                  </legend>
                  @for (item of requirementsFormControls; track item) {
                    <textarea
                      class="daisy-textarea daisy-textarea-bordered w-full"
                      cols="1"
                      [formControl]="item"
                      placeholder="e.g. Basic knowledge of algebra and arithmetic"
                    >
                    </textarea>
                  }
                  <button
                    (click)="addMoreRequirementInputs()"
                    class="daisy-btn daisy-btn-sm"
                  >
                    Add More
                  </button>
                </fieldset>
              </div>
              <div class="w-full mt-4" formArrayName="coverage">
                <fieldset class="daisy-fieldset">
                  <legend class="daisy-fieldset-legend">
                    Outcomes
                    <black-tooltip
                      >Include what are the expected outcomes of this
                      course</black-tooltip
                    >
                  </legend>
                  @for (item of coverageFormControls; track item) {
                    <textarea
                      cols="1"
                      class="daisy-textarea daisy-textarea-bordered w-full"
                      [formControl]="item"
                      type="text"
                      placeholder="e.g. Students will be able to solve quadratic equations confidently"
                    ></textarea>
                  }
                  <button
                    (click)="addMoreCoverageInputs()"
                    class="daisy-btn daisy-btn-sm"
                  >
                    Add More
                  </button>
                </fieldset>
              </div>
            </div>
          </div>

          <div id="course-settings" class="mt-8" formGroupName="settings">
            <h4 class="text-2xl font-bold text-base-content">Class Settings</h4>

            <div class="container">
              <div class="flex flex-wrap">
                <div class="w-full mt-4">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      Maximum Students
                    </legend>
                    <div class="daisy-join w-full">
                      <button
                        type="button"
                        (click)="decreaseCount()"
                        class="daisy-btn daisy-join-item"
                      >
                        -
                      </button>
                      <input
                        formControlName="maxStudents"
                        class="daisy-input daisy-input-bordered daisy-join-item w-full"
                        type="number"
                        value="100"
                        min="1"
                      />
                      <button
                        type="button"
                        (click)="increaseCount()"
                        class="daisy-btn daisy-join-item"
                      >
                        +
                      </button>
                    </div>
                  </fieldset>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mt-4">
                  <div class="w-full">
                    <fieldset class="daisy-fieldset">
                      <legend class="daisy-fieldset-legend">
                        Max Session Duration
                        <black-tooltip
                          >Automatically calculated as the longest session
                          duration from your meeting schedules</black-tooltip
                        >
                      </legend>
                      <div
                        class="daisy-input daisy-input-bordered w-full flex items-center gap-2 bg-base-200 cursor-not-allowed"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4 text-base-content/50"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span class="text-base-content/70">{{
                          formatDuration(calculatedMaxDuration)
                        }}</span>
                      </div>
                      @if (meetingSchedules.length === 0) {
                        <p class="text-sm text-base-content/50 mt-1">
                          Add meeting schedules below to set the session
                          duration.
                        </p>
                      }
                    </fieldset>
                  </div>
                  <div class="w-full">
                    <div class="flex items-center justify-between mb-1.5">
                      <label class="font-semibold text-sm text-base-content flex items-center gap-1.5">
                        <span>Price per Session</span>
                        <black-tooltip
                          >Set the price for each session of this
                          class</black-tooltip
                        >
                      </label>
                      @if (isPublished) {
                        <span
                          class="daisy-badge daisy-badge-warning daisy-badge-sm gap-1 font-medium shrink-0"
                        >
                          <i class="fa-solid fa-lock text-[10px]"></i> Locked
                        </span>
                      }
                    </div>
                    <input
                      class="daisy-input daisy-input-bordered w-full"
                      [ngClass]="{
                        'bg-base-200 cursor-not-allowed opacity-80': isPublished,
                      }"
                      formControlName="price"
                      type="number"
                      placeholder="Set 0 for free"
                      min="0"
                    />
                  </div>
                </div>

                <!-- Visibility / Public vs Unlisted -->
                <div
                  class="w-full mt-4 border border-base-300 rounded-xl p-4 bg-base-200/40"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <span
                        class="font-semibold text-sm text-base-content flex items-center gap-2"
                      >
                        <i class="fa-solid fa-globe text-primary"></i>
                        Catalog Visibility
                      </span>
                      <p class="text-xs text-base-content/60 mt-0.5">
                        {{
                          createLiveClassForm.get('settings.isPublic')?.value
                            ? 'Public: Listed on the website catalog and explore classes'
                            : 'Unlisted (Private): Hidden from catalog, accessible only via direct link or batch'
                        }}
                      </p>
                    </div>
                    <label class="daisy-swap daisy-swap-rotate">
                      <input
                        type="checkbox"
                        formControlName="isPublic"
                        class="daisy-toggle daisy-toggle-primary"
                      />
                    </label>
                  </div>
                </div>

                <div class="w-full mt-4">
                  <label class="font-semibold text-base-content block mb-1"
                    >Banner
                    <black-tooltip
                      >Should be an image with 16:9 aspect ratio</black-tooltip
                    ></label
                  >
                  <div class="file-pond-wrapper">
                    <file-pond
                      #myPond
                      [options]="pondOptions"
                      (onprocessfile)="pondHandleProcessFile($event)"
                      (onremovefile)="pondHandleRevertFile($event)"
                      [files]="pondFiles"
                    >
                    </file-pond>
                  </div>
                  @if (
                    createLiveClassForm.get('settings.banner')?.invalid &&
                    createLiveClassForm.get('settings.banner')?.touched
                  ) {
                    <p class="text-error text-xs mt-1 font-medium">
                      Banner is required
                    </p>
                  }
                </div>
              </div>
            </div>
          </div>

          <black-schedule-form
            [meetingSchedules]="meetingSchedules"
            [isLocked]="isPublished"
            (addSchedule)="syncMeetingsLength()"
            (removeSchedule)="syncMeetingsLength()"
          ></black-schedule-form>

          <div
            id="course-social-links"
            class="mt-8"
            formGroupName="socialLinks"
          >
            <h4 class="text-2xl font-bold text-base-content">
              Community Links
            </h4>

            <!-- Info Card -->
            <div
              class="daisy-alert daisy-alert-info bg-info/10 border border-info/20 text-base-content mt-4 flex items-start gap-3"
            >
              <i class="fa-solid fa-circle-info text-info mt-0.5 text-base"></i>
              <div>
                <p class="font-semibold text-base-content">
                  Tip: Add a group invite link
                </p>
                <p class="text-xs text-base-content/80 mt-0.5">
                  It is recommended to add a WhatsApp or Telegram group invite
                  link so subscribed students can join your class community for
                  announcements, updates, and quick Q&amp;A. These links are
                  only visible to enrolled students.
                </p>
              </div>
            </div>

            <div class="container mt-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      WhatsApp Group Link
                      <span
                        class="daisy-badge daisy-badge-ghost daisy-badge-sm ml-1"
                        >Optional</span
                      >
                    </legend>
                    <input
                      type="url"
                      class="daisy-input daisy-input-bordered w-full"
                      formControlName="whatsapp"
                      placeholder="e.g. https://chat.whatsapp.com/xxxxx"
                    />
                  </fieldset>
                </div>
                <div class="w-full">
                  <fieldset class="daisy-fieldset">
                    <legend class="daisy-fieldset-legend">
                      Telegram Group Link
                      <span
                        class="daisy-badge daisy-badge-ghost daisy-badge-sm ml-1"
                        >Optional</span
                      >
                    </legend>
                    <input
                      type="url"
                      class="daisy-input daisy-input-bordered w-full"
                      formControlName="telegram"
                      placeholder="e.g. https://t.me/xxxxx"
                    />
                  </fieldset>
                </div>
              </div>
            </div>
          </div>

          <div id="course-submit" class="mt-8 mb-8">
            <div class="container">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="w-full md:col-span-1">
                  @if (this.liveClassId) {
                    <a
                      class="daisy-btn daisy-btn-outline daisy-btn-secondary daisy-btn-block"
                      [routerLink]="'/live-classes/' + this.liveClassId"
                      >Preview</a
                    >
                  }
                </div>
                <div class="w-full md:col-span-2">
                  <a
                    (click)="submit()"
                    class="daisy-btn daisy-btn-primary daisy-btn-block"
                    >Save & Continue</a
                  >
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>
